import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AdminBar from '../components/AdminBar';
import { useThemeMode } from '../components/ThemeModeContext';
import { serverDomain } from './config';

const GRID_SIZE = 64;
// original palette...
/* const PALETTE = [
  '#000000', '#4B5563', '#9CA3AF', '#FFFFFF',
  '#F87171', '#F59E0B', '#FBBF24', '#34D399', '#10B981', '#14B8A6',
  '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899', '#F472B6', '#EF4444',
  '#1D4ED8', '#06B6D4', '#A3E635', '#22C55E'
]; */

// new pallette
const PALETTE = [
  // Neutrals (4)
  '#000000', '#555555', '#AAAAAA', '#FFFFFF',

  // Primary spectrum anchors (6 — your required colours)
  '#FF0000', // red
  '#FFA500', // orange
  '#FFFF00', // yellow
  '#00FF00', // green
  '#00FFFF', // cyan
  '#0000FF', // blue

  // Secondary / deep tones (6)
  '#800000', // dark red
  '#FF69B4', // hot pink
  '#800080', // purple
  '#4B0082', // indigo
  '#008080', // teal
  '#006400', // dark green

  // Light tints for highlights (4)
  '#FFCCCC', // light red
  '#FFE5B4', // light orange/peach
  '#FFFFCC', // light yellow
  '#CCFFFF'  // light cyan
];


export default function IconEditor() {
  const { isDarkMode } = useThemeMode();
  const navigate = useNavigate();
  const headerBg = isDarkMode ? '#000000' : '#003366';
  const headerText = '#ffffff';
  const pageBg = isDarkMode ? '#1a1a1a' : '#ffffff';
  const pageText = isDarkMode ? '#f5f5f5' : '#000000';

  const [gridData, setGridData] = useState(
    Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(null))
  );
  const [currentColor, setCurrentColor] = useState(PALETTE[0]);
  const [toolMode, setToolMode] = useState('pen');
  const [brushSize, setBrushSize] = useState(1);
  const [isPainting, setIsPainting] = useState(false);
  const [shapeStart, setShapeStart] = useState(null);
  const [shapePreviewCells, setShapePreviewCells] = useState([]);
  const [lastPointerCell, setLastPointerCell] = useState(null);
  const [savingAction, setSavingAction] = useState(null);
  const [error, setError] = useState('');
  const [oldIconStyle, setOldIconStyle] = useState({ background: '#ccc', textColor: '#000' });
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [isDesktop, setIsDesktop] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= 1024 : true));
  const [isMediumUp, setIsMediumUp] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= 769 : true));
  const gridRef = useRef(null);

  const cellSize = 5;
  const gridTemplate = useMemo(() => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${GRID_SIZE}, ${cellSize}px)`,
    gridAutoRows: `${cellSize}px`,
    border: `1px solid ${isDarkMode ? '#334155' : '#d1d5db'}`
  }), [isDarkMode]);

  const actionButtonStyle = {
    background: '#3a3a3a',
    color: '#ffffff',
    border: '1px solid #4a4a4a',
    borderRadius: 8,
    padding: isDesktop ? '10px 12px' : '6px 8px',
    fontWeight: 600,
    cursor: 'pointer'
  };

  const toolButtonStyle = {
    ...actionButtonStyle,
    padding: isDesktop ? '6px 12px' : '4px 8px',
    height: isDesktop ? 34 : 30,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const saving = savingAction !== null;
  const previewCellSet = useMemo(() => {
    const set = new Set();
    for (let i = 0; i < shapePreviewCells.length; i++) {
      const point = shapePreviewCells[i];
      set.add(`${point.x},${point.y}`);
    }
    return set;
  }, [shapePreviewCells]);

  const getModeButtonStyle = (mode) => ({
    ...toolButtonStyle,
    background: toolMode === mode ? '#93c5fd' : toolButtonStyle.background,
    border: toolMode === mode ? '1px solid #60a5fa' : toolButtonStyle.border,
    color: toolMode === mode ? '#0f172a' : toolButtonStyle.color
  });

  const getBrushButtonStyle = (size) => ({
    ...toolButtonStyle,
    minWidth: isDesktop ? 62 : 56,
    padding: isDesktop ? '4px 8px' : '3px 6px',
    height: isDesktop ? 30 : 28,
    background: brushSize === size ? '#93c5fd' : toolButtonStyle.background,
    border: brushSize === size ? '1px solid #60a5fa' : toolButtonStyle.border,
    color: brushSize === size ? '#0f172a' : toolButtonStyle.color
  });

  const getToolIcon = (mode) => {
    if (mode === 'pen') return '✎';
    if (mode === 'eraser') return '⌫';
    if (mode === 'line') return '／';
    if (mode === 'square') return '□';
    if (mode === 'circle') return '○';
    if (mode === 'fill') return '🪣';
    return '?';
  };

  const squareIconPixels = isDesktop ? 20 : 18;
  const circleIconPixels = isDesktop ? 20 : 19;

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsMediumUp(window.innerWidth >= 769);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Convert RGB values to hex color string
  const rgbToHex = (r, g, b) => {
    const toHex = (v) => v.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const cloneGrid = (sourceGrid) => sourceGrid.map((row) => row.slice());

  // Load existing icon_data (data URL) if present for current app_id
  React.useEffect(() => {
    const token = localStorage.getItem('appstar_jwt');
    const app_id = localStorage.getItem('appstar_app_id');
    if (!token || !app_id) return;

    let cancelled = false;
    axios.post(serverDomain + 'get-all-apps.php', null, { params: { token } })
      .then((res) => {
        if (cancelled) return;
        const apps = res?.data?.data?.apps || [];
        const app = apps.find(a => String(a.app_id) === String(app_id));
        const iconData = app?.icon_data;
        try {
          setOldIconStyle(JSON.parse(app?.icon_style || '{}'));
        } catch {
          setOldIconStyle({ background: '#ccc', textColor: '#000' });
        }
        if (!iconData) return;

        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          const cellW = canvas.width / GRID_SIZE;
          const cellH = canvas.height / GRID_SIZE;
          const next = Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(null));
          for (let y = 0; y < GRID_SIZE; y++) {
            for (let x = 0; x < GRID_SIZE; x++) {
              const sx = Math.floor((x + 0.5) * cellW);
              const sy = Math.floor((y + 0.5) * cellH);
              try {
                const p = ctx.getImageData(sx, sy, 1, 1).data;
                const [r, g, b, a] = p;
                if (a === 0) {
                  next[y][x] = null;
                } else {
                  next[y][x] = rgbToHex(r, g, b);
                }
              } catch (e) {
                // getImageData can throw if coordinate out of bounds; ignore and leave null
                next[y][x] = null;
              }
            }
          }
          setGridData(next);
          setUndoStack([]);
          setRedoStack([]);
        };
        img.onerror = () => {};
        img.src = iconData;
      })
      .catch(() => {})
      .finally(() => {});

    return () => { cancelled = true; };
  }, []);

  const paint = (x, y) => {
    setGridData(prev => {
      const points = expandPointsWithBrush([{ x, y }], brushSize);
      const next = cloneGrid(prev);
      const nextColor = toolMode === 'eraser' ? null : currentColor;
      const changes = [];

      for (let i = 0; i < points.length; i++) {
        const point = points[i];
        const previousColor = prev[point.y][point.x];
        if (previousColor === nextColor) continue;
        next[point.y][point.x] = nextColor;
        changes.push({ x: point.x, y: point.y, previousColor, nextColor });
      }

      if (changes.length === 0) return prev;
      setUndoStack(stack => [...stack, {
        type: 'changes',
        changes
      }]);
      setRedoStack([]);
      return next;
    });
  };

  const makeKey = (x, y) => `${x},${y}`;

  const uniqueInBoundsPoints = (points) => {
    const seen = new Set();
    const result = [];
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      if (point.x < 0 || point.y < 0 || point.x >= GRID_SIZE || point.y >= GRID_SIZE) continue;
      const key = makeKey(point.x, point.y);
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(point);
    }
    return result;
  };

  const expandPointsWithBrush = (points, size) => {
    if (!points || points.length === 0) return [];
    if (size <= 1) return uniqueInBoundsPoints(points);
    const expanded = [];
    const startOffset = -Math.floor(size / 2);
    const endOffset = startOffset + size - 1;

    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      for (let offsetY = startOffset; offsetY <= endOffset; offsetY++) {
        for (let offsetX = startOffset; offsetX <= endOffset; offsetX++) {
          expanded.push({ x: point.x + offsetX, y: point.y + offsetY });
        }
      }
    }

    return uniqueInBoundsPoints(expanded);
  };

  const getLinePoints = (start, end) => {
    const points = [];
    let x0 = start.x;
    let y0 = start.y;
    const x1 = end.x;
    const y1 = end.y;
    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;

    while (true) {
      points.push({ x: x0, y: y0 });
      if (x0 === x1 && y0 === y1) break;
      const e2 = err * 2;
      if (e2 > -dy) {
        err -= dy;
        x0 += sx;
      }
      if (e2 < dx) {
        err += dx;
        y0 += sy;
      }
    }
    return uniqueInBoundsPoints(points);
  };

  const getSquarePoints = (start, end) => {
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);
    const points = [];

    for (let x = minX; x <= maxX; x++) {
      points.push({ x, y: minY });
      points.push({ x, y: maxY });
    }
    for (let y = minY; y <= maxY; y++) {
      points.push({ x: minX, y });
      points.push({ x: maxX, y });
    }
    return uniqueInBoundsPoints(points);
  };

  const getCirclePoints = (start, end) => {
    const points = [];
    const radius = Math.max(Math.abs(end.x - start.x), Math.abs(end.y - start.y));
    if (radius === 0) return [{ x: start.x, y: start.y }];

    let x = radius;
    let y = 0;
    let decision = 1 - x;

    while (y <= x) {
      points.push({ x: start.x + x, y: start.y + y });
      points.push({ x: start.x + y, y: start.y + x });
      points.push({ x: start.x - y, y: start.y + x });
      points.push({ x: start.x - x, y: start.y + y });
      points.push({ x: start.x - x, y: start.y - y });
      points.push({ x: start.x - y, y: start.y - x });
      points.push({ x: start.x + y, y: start.y - x });
      points.push({ x: start.x + x, y: start.y - y });
      y++;
      if (decision <= 0) {
        decision += (2 * y) + 1;
      } else {
        x--;
        decision += (2 * (y - x)) + 1;
      }
    }
    return uniqueInBoundsPoints(points);
  };

  const getShapePoints = (mode, start, end) => {
    if (!start || !end) return [];
    let basePoints = [];
    if (mode === 'line') basePoints = getLinePoints(start, end);
    if (mode === 'square') basePoints = getSquarePoints(start, end);
    if (mode === 'circle') basePoints = getCirclePoints(start, end);
    if (basePoints.length === 0) return [];
    return expandPointsWithBrush(basePoints, brushSize);
  };

  const applyPoints = (points, nextColor) => {
    if (!points || points.length === 0) return;
    setGridData(prev => {
      const previousGrid = cloneGrid(prev);
      const next = cloneGrid(prev);
      let changed = false;
      for (let i = 0; i < points.length; i++) {
        const point = points[i];
        if (next[point.y][point.x] === nextColor) continue;
        next[point.y][point.x] = nextColor;
        changed = true;
      }
      if (!changed) return prev;
      setUndoStack(stack => [...stack, {
        type: 'snapshot',
        previousGrid,
        nextGrid: cloneGrid(next)
      }]);
      setRedoStack([]);
      return next;
    });
  };

  const completeShape = () => {
    const shapeModes = ['line', 'square', 'circle'];
    if (!shapeModes.includes(toolMode)) {
      setIsPainting(false);
      return;
    }
    if (!shapeStart || !lastPointerCell) {
      setIsPainting(false);
      setShapeStart(null);
      setShapePreviewCells([]);
      return;
    }
    const points = getShapePoints(toolMode, shapeStart, lastPointerCell);
    applyPoints(points, currentColor);
    setIsPainting(false);
    setShapeStart(null);
    setShapePreviewCells([]);
  };

  const floodFill = (startX, startY) => {
    setGridData(prev => {
      const fillColor = toolMode === 'eraser' ? null : currentColor;
      const targetColor = prev[startY][startX];
      if (targetColor === fillColor) return prev;

      const previousGrid = cloneGrid(prev);
      const next = cloneGrid(prev);
      const queue = [[startX, startY]];
      const visited = new Set();
      const changes = [];

      while (queue.length > 0) {
        const [x, y] = queue.pop();
        const key = `${x},${y}`;
        if (visited.has(key)) continue;
        visited.add(key);

        if (x < 0 || y < 0 || x >= GRID_SIZE || y >= GRID_SIZE) continue;
        if (next[y][x] !== targetColor) continue;

        changes.push({ x, y, previousColor: targetColor, nextColor: fillColor });
        next[y][x] = fillColor;

        queue.push([x + 1, y]);
        queue.push([x - 1, y]);
        queue.push([x, y + 1]);
        queue.push([x, y - 1]);
      }

      if (changes.length === 0) return prev;
      setUndoStack(stack => [...stack, {
        type: 'snapshot',
        previousGrid,
        nextGrid: cloneGrid(next)
      }]);
      setRedoStack([]);
      return next;
    });
  };

  const handlePointerDown = (x, y) => {
    const pointerCell = { x, y };
    setLastPointerCell(pointerCell);
    if (toolMode === 'fill') {
      setIsPainting(false);
      setShapeStart(null);
      setShapePreviewCells([]);
      floodFill(x, y);
      return;
    }
    if (toolMode === 'line' || toolMode === 'square' || toolMode === 'circle') {
      setIsPainting(true);
      setShapeStart(pointerCell);
      setShapePreviewCells(getShapePoints(toolMode, pointerCell, pointerCell));
      return;
    }
    setIsPainting(true);
    paint(x, y);
  };

  const handlePointerEnter = (x, y) => {
    if (toolMode === 'fill') return;
    const pointerCell = { x, y };
    setLastPointerCell(pointerCell);
    if ((toolMode === 'line' || toolMode === 'square' || toolMode === 'circle') && isPainting && shapeStart) {
      setShapePreviewCells(getShapePoints(toolMode, shapeStart, pointerCell));
      return;
    }
    if (isPainting) paint(x, y);
  };

  const handlePointerUp = () => {
    completeShape();
    setIsPainting(false);
  };

  React.useEffect(() => {
    const stopPaint = () => handlePointerUp();
    window.addEventListener('mouseup', stopPaint);
    window.addEventListener('touchend', stopPaint);
    return () => {
      window.removeEventListener('mouseup', stopPaint);
      window.removeEventListener('touchend', stopPaint);
    };
  }, [toolMode, isPainting, shapeStart, lastPointerCell, currentColor]);

  React.useEffect(() => {
    setShapeStart(null);
    setShapePreviewCells([]);
    setIsPainting(false);
  }, [toolMode, brushSize]);

  const clearGrid = () => {
    setGridData(prev => {
      const previousGrid = cloneGrid(prev);
      const clearedGrid = Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(null));
      let hasAnyColor = false;
      for (let y = 0; y < GRID_SIZE; y++) {
        for (let x = 0; x < GRID_SIZE; x++) {
          if (prev[y][x] !== null) {
            hasAnyColor = true;
            break;
          }
        }
        if (hasAnyColor) break;
      }
      if (!hasAnyColor) return prev;
      setUndoStack(stack => [...stack, {
        type: 'snapshot',
        previousGrid,
        nextGrid: cloneGrid(clearedGrid)
      }]);
      setRedoStack([]);
      return clearedGrid;
    });
    setShapeStart(null);
    setShapePreviewCells([]);
  };

  const buildPngDataUrlFromGrid = (sourceGrid) => {
    const scale = 16;
    const canvas = document.createElement('canvas');
    canvas.width = GRID_SIZE * scale;
    canvas.height = GRID_SIZE * scale;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (let y = 0; y < GRID_SIZE; y++) {
      for (let x = 0; x < GRID_SIZE; x++) {
        const color = sourceGrid[y][x];
        if (!color) continue;
        ctx.fillStyle = color;
        ctx.fillRect(x * scale, y * scale, scale, scale);
      }
    }
    return canvas.toDataURL('image/png');
  };

  const buildPngDataUrl = () => {
    return buildPngDataUrlFromGrid(gridData);
  };

  const currentPreviewData = useMemo(() => buildPngDataUrlFromGrid(gridData), [gridData]);

  const undoLastDot = () => {
    const last = undoStack[undoStack.length - 1];
    if (!last) return;
    if (last.type === 'snapshot') {
      setGridData(cloneGrid(last.previousGrid));
    } else {
      setGridData(prev => {
        const next = cloneGrid(prev);
        for (let i = last.changes.length - 1; i >= 0; i--) {
          const change = last.changes[i];
          next[change.y][change.x] = change.previousColor;
        }
        return next;
      });
    }
    setUndoStack(stack => stack.slice(0, -1));
    setRedoStack(stack => [...stack, last]);
  };

  const redoLastDot = () => {
    const last = redoStack[redoStack.length - 1];
    if (!last) return;
    if (last.type === 'snapshot') {
      setGridData(cloneGrid(last.nextGrid));
    } else {
      setGridData(prev => {
        const next = cloneGrid(prev);
        for (let i = 0; i < last.changes.length; i++) {
          const change = last.changes[i];
          next[change.y][change.x] = change.nextColor;
        }
        return next;
      });
    }
    setRedoStack(stack => stack.slice(0, -1));
    setUndoStack(stack => [...stack, last]);
  };

  const keepIcon = async () => {
    const token = localStorage.getItem('appstar_jwt');
    const app_id = localStorage.getItem('appstar_app_id');
    if (!token || !app_id) {
      setError('Missing session or app id.');
      return;
    }

    setSavingAction('keep');
    setError('');
    try {
      const icon_data = buildPngDataUrl();
      const payload = { token, app_id, icon_data };
      await axios.post(serverDomain + 'update-app-icon.php', payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      navigate('/');
    } catch (e) {
      console.error('Icon save error', e);
      setError('Could not save icon. Please try again.');
    } finally {
      setSavingAction(null);
    }
  };

  const panelCardStyle = {
    border: `1px solid ${isDarkMode ? '#374151' : '#d1d5db'}`,
    borderRadius: 12,
    padding: isDesktop ? '12px' : '10px',
    background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc'
  };

  const sectionTitleStyle = {
    fontSize: '0.9rem',
    fontWeight: 700,
    opacity: 0.92,
    marginBottom: 8
  };

  const isMediumScreen = isMediumUp && !isDesktop;

  return (
    <div style={{ minHeight: '100vh', background: pageBg, color: pageText }}>
      <AdminBar bgColor={headerBg} textColor={headerText} appName="Edit Icon" centerAppName={isMediumUp} />
      <style>{`@keyframes iconEditorSpin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isDesktop ? '24px' : '14px' }}>
        <div style={{ display: 'flex', gap: isDesktop ? 24 : 14, flexDirection: isMediumUp ? 'row' : 'column', alignItems: 'stretch' }}>
          <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ ...panelCardStyle, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ ...sectionTitleStyle, width: '100%', marginBottom: 10 }}>Canvas</div>
              <div
                ref={gridRef}
                style={gridTemplate}
                onMouseLeave={() => setIsPainting(false)}
                onDragStart={(e) => e.preventDefault()}
              >
                {gridData.map((row, y) =>
                  row.map((color, x) => (
                    <div
                      key={`${x}-${y}`}
                      onMouseDown={(e) => { e.preventDefault(); handlePointerDown(x, y); }}
                      onMouseEnter={(e) => {
                        if (isPainting && e.buttons === 0) {
                          handlePointerUp();
                          return;
                        }
                        handlePointerEnter(x, y);
                      }}
                      onTouchStart={(e) => { e.preventDefault(); handlePointerDown(x, y); }}
                      onTouchMove={(e) => {
                        e.preventDefault();
                        const touch = e.touches?.[0];
                        if (!touch) return;
                        const el = document.elementFromPoint(touch.clientX, touch.clientY);
                        const tx = Number(el?.getAttribute?.('data-x'));
                        const ty = Number(el?.getAttribute?.('data-y'));
                        if (Number.isInteger(tx) && Number.isInteger(ty)) handlePointerEnter(tx, ty);
                      }}
                      data-x={x}
                      data-y={y}
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      style={{
                        width: cellSize,
                        height: cellSize,
                        boxSizing: 'border-box',
                        border: '1px solid rgba(255,255,255,0.08)',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        touchAction: 'none',
                        backgroundColor: previewCellSet.has(`${x},${y}`) ? currentColor : (color || 'transparent'),
                        opacity: previewCellSet.has(`${x},${y}`) && !color ? 0.65 : 1
                      }}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          <div style={{ flex: isDesktop ? '0 0 360px' : '0 0 320px', width: isMediumUp ? 'auto' : '100%', minWidth: isMediumUp ? 300 : 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={panelCardStyle}>
              <div style={sectionTitleStyle}>Tools</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <button type="button" onClick={() => setToolMode('pen')} style={{ ...getModeButtonStyle('pen'), width: 38, padding: 0 }} title="Pen" aria-label="Pen">
                  <span aria-hidden="true" style={{ fontSize: '1.05rem', lineHeight: 1 }}>{getToolIcon('pen')}</span>
                </button>
                <button type="button" onClick={() => setToolMode('eraser')} style={{ ...getModeButtonStyle('eraser'), width: 38, padding: 0 }} title="Eraser" aria-label="Eraser">
                  <span aria-hidden="true" style={{ fontSize: '1rem', lineHeight: 1 }}>{getToolIcon('eraser')}</span>
                </button>
                <button type="button" onClick={() => setToolMode('line')} style={{ ...getModeButtonStyle('line'), width: 38, padding: 0 }} title="Line" aria-label="Line">
                  <span aria-hidden="true" style={{ fontSize: '1.05rem', lineHeight: 1 }}>{getToolIcon('line')}</span>
                </button>
                <button type="button" onClick={() => setToolMode('square')} style={{ ...getModeButtonStyle('square'), width: 38, padding: 0 }} title="Square" aria-label="Square">
                  <span aria-hidden="true" style={{ width: '100%', height: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width={squareIconPixels} height={squareIconPixels} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
                      <rect x="4" y="4" width="16" height="16" stroke="currentColor" strokeWidth="2.75" />
                    </svg>
                  </span>
                </button>
                <button type="button" onClick={() => setToolMode('circle')} style={{ ...getModeButtonStyle('circle'), width: 38, padding: 0 }} title="Circle" aria-label="Circle">
                  <span aria-hidden="true" style={{ width: '100%', height: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width={circleIconPixels} height={circleIconPixels} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
                      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2.75" />
                    </svg>
                  </span>
                </button>
                <button type="button" onClick={() => setToolMode('fill')} style={{ ...getModeButtonStyle('fill'), width: 38, padding: 0 }} title="Flood fill" aria-label="Flood fill">
                  <span aria-hidden="true" style={{ fontSize: '1rem', lineHeight: 1 }}>{getToolIcon('fill')}</span>
                </button>
                <button type="button" onClick={clearGrid} style={toolButtonStyle}>Clear</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, opacity: 0.9 }}>Brush</span>
                <button type="button" onClick={() => setBrushSize(1)} style={getBrushButtonStyle(1)} title="1px brush">1px</button>
                <button type="button" onClick={() => setBrushSize(2)} style={getBrushButtonStyle(2)} title="2x2 brush">2x2</button>
                <button type="button" onClick={() => setBrushSize(4)} style={getBrushButtonStyle(4)} title="4x4 brush">4x4</button>
                <button type="button" onClick={() => setBrushSize(8)} style={getBrushButtonStyle(8)} title="8x8 brush">8x8</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 68,
                      height: 34,
                      borderRadius: 6,
                      border: '2px solid rgba(255,255,255,0.45)',
                      background: toolMode === 'eraser' ? 'transparent' : currentColor,
                      backgroundImage: toolMode === 'eraser' ? 'linear-gradient(45deg, #888 25%, transparent 25%, transparent 50%, #888 50%, #888 75%, transparent 75%, transparent)' : 'none',
                      backgroundSize: toolMode === 'eraser' ? '8px 8px' : 'auto'
                    }}
                  />
                  <span style={{ fontSize: '0.9rem', opacity: 0.9 }}>Selected</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={undoLastDot}
                    disabled={undoStack.length === 0}
                    style={{ ...toolButtonStyle, paddingLeft: isDesktop ? '8px' : '6px', cursor: undoStack.length === 0 ? 'not-allowed' : 'pointer' }}
                    title="Undo"
                  >
                    <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>↶</span> Undo
                  </button>
                  <button
                    type="button"
                    onClick={redoLastDot}
                    disabled={redoStack.length === 0}
                    style={{ ...toolButtonStyle, paddingLeft: isDesktop ? '8px' : '6px', cursor: redoStack.length === 0 ? 'not-allowed' : 'pointer' }}
                    title="Redo"
                  >
                    <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>↷</span> Redo
                  </button>
                </div>
              </div>
            </div>

            <div style={panelCardStyle}>
              <div style={sectionTitleStyle}>Colors</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {PALETTE.map((col) => (
                  <button
                    key={col}
                    type="button"
                    onClick={() => { setCurrentColor(col); }}
                    style={{
                      width: 25,
                      height: 25,
                      borderRadius: 6,
                      border: currentColor === col && toolMode !== 'eraser' ? '3px solid #93c5fd' : '1px solid rgba(255,255,255,0.35)',
                      background: col,
                      cursor: 'pointer'
                    }}
                  />
                ))}
              </div>
            </div>

            <div style={panelCardStyle}>
              <div style={sectionTitleStyle}>Actions</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexDirection: isDesktop ? 'row' : 'column' }}>
                <button type="button" onClick={() => navigate('/')} disabled={saving} style={{ ...actionButtonStyle, width: isDesktop ? 'auto' : '100%' }}>Discard</button>
                <button
                  type="button"
                  onClick={async () => {
                    const token = localStorage.getItem('appstar_jwt');
                    const app_id = localStorage.getItem('appstar_app_id');
                    if (!token || !app_id) {
                      setError('Missing session or app id.');
                      return;
                    }
                    setSavingAction('remove');
                    try {
                      const payload = { token, app_id, icon_data: '' };
                      const res = await axios.post(serverDomain + 'update-app-icon.php', payload, { headers: { 'Content-Type': 'application/json' } });
                      console.log('remove icon response', res?.data);
                      navigate('/');
                    } catch (e) {
                      console.error('Remove icon error', e);
                      setError('Could not remove icon. Please try again.');
                    } finally {
                      setSavingAction(null);
                    }
                  }}
                  disabled={saving}
                  style={{ ...actionButtonStyle, whiteSpace: 'nowrap', width: isDesktop ? 'auto' : '100%' }}
                >Remove custom icon</button>
                <button
                  type="button"
                  onClick={keepIcon}
                  disabled={saving}
                  style={{
                    ...actionButtonStyle,
                    whiteSpace: 'nowrap',
                    width: isDesktop ? 'auto' : '100%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    background: '#2563eb',
                    border: '1px solid #1d4ed8',
                    color: '#ffffff',
                    minHeight: !isDesktop ? 40 : undefined
                  }}
                >
                  {savingAction === 'keep' ? 'Saving...' : 'Keep Icon'}
                </button>
              </div>
            </div>

            <div style={panelCardStyle}>
              <div style={sectionTitleStyle}>Preview</div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '0.9rem', marginBottom: 8 }}>Custom icon</div>
                  <img
                    src={currentPreviewData}
                    alt="Current icon preview"
                    style={{ width: 77, height: 77, borderRadius: 8, border: '1px solid rgba(255,255,255,0.25)', imageRendering: 'pixelated', display: 'block' }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: '0.9rem', marginBottom: 8 }}>Base icon</div>
                  <div
                    style={{
                      width: 77,
                      height: 77,
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.25)',
                      background: oldIconStyle.background || '#ccc',
                      color: oldIconStyle.textColor || '#000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 34,
                      fontWeight: 700,
                      userSelect: 'none'
                    }}
                  >
                    {(localStorage.getItem('appstar_app_name') || 'A').charAt(0).toUpperCase()}
                  </div>
                </div>
              </div>
            </div>

            {error && <p style={{ color: '#ef4444', margin: 0 }}>{error}</p>}
          </div>
        </div>
      </div>

      {savingAction === 'keep' && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            background: isDarkMode ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.32)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            aria-label="Saving icon"
            style={{
              width: 48,
              height: 48,
              border: '4px solid rgba(0,0,0,0.1)',
              borderTop: `4px solid ${isDarkMode ? '#ffffff' : '#003366'}`,
              borderRadius: '50%',
              animation: 'iconEditorSpin 1s linear infinite'
            }}
          />
        </div>
      )}
    </div>
  );
}
